/* -------------------------------------------------------------------------- */
/*                                Select styles                               */
/* -------------------------------------------------------------------------- */

@import './bq-select.variables';

:host {
  @apply block w-full;
}

/* -------------------------------------------------------------------------- */
/*                            Label and Helper text                           */
/* -------------------------------------------------------------------------- */

.bq-select__label {
  @apply flex items-center m-be-[--bq-select--label-margin-bottom];
  @apply text-[length:--bq-select--label-text-size] text-[color:--bq-select--label-text-color];
}

.bq-select__helper-text {
  @apply text-[length:--bq-select--helper-text-size] text-[color:--bq-select--helper-text-color] m-bs-[--bq-select--helper-margin-top];
}

.bq-select__helper-text.validation-error {
  @apply text-danger;
}

.bq-select__helper-text.validation-success {
  @apply text-success;
}

.bq-select__helper-text.validation-warning {
  @apply text-warning;
}

/* -------------------------------------------------------------------------- */
/*                         Select input group control                         */
/* -------------------------------------------------------------------------- */

.bq-select__dropdown:has(:focus-within, :focus-visible) {
  // The focus state will be controlled by the dropdown element
  // if any of its children has focus, the dropdown will set the focus state to the .bq-select__control
  .bq-select__control {
    --bq-ring-width: 1px;
    --bq-ring-offset-width: 0;
    --bq-ring-color-focus: var(--bq-select--border-color-focus);

    @apply focus border-[color:--bq-select--border-color-focus];
  }
}

.bq-select__control {
  @apply flex items-center transition-[border-color,box-shadow] is-full;
  // Border
  @apply rounded-[--bq-select--border-radius] border-[length:--bq-select--border-width] border-[color:--bq-select--border-color];
  // Padding
  @apply pe-[--bq-select--padding-end] ps-[--bq-select--padding-start] p-b-[var(--bq-select--paddingY)];
  // Text
  @apply select-none text-[length:--bq-select--text-size] text-[color:--bq-select--text-color] placeholder:text-[color:--bq-select--text-placeholder-color];
  // Hover
  @apply [&:not(.disabled):not(:focus-within)]:hover:border-hover-brand;

  border-style: var(--bq-select--border-style);

  // Enable clear button whenever the input group control is hovered or has focus
  &:not(.disabled):hover,
  &:not(.disabled):focus-within {
    .bq-select__control--clear {
      @apply inline-block;
    }
  }
}

.bq-select__control.disabled {
  @apply cursor-not-allowed opacity-60;
}

/* ------------------------------- Validation ------------------------------- */

.bq-select__control.validation-error {
  @apply border-danger [&:not(.disabled):not(:focus-within)]:hover:border-hover-danger;

  &:not(.disabled):focus-within {
    --bq-ring-color-focus: theme(stroke.danger);

    @apply border-active-danger;
  }
}

.bq-select__control.validation-success {
  @apply border-success [&:not(.disabled):not(:focus-within)]:hover:border-hover-success;

  &:not(.disabled):focus-within {
    --bq-ring-color-focus: theme(stroke.success);

    @apply border-active-success;
  }
}

.bq-select__control.validation-warning {
  @apply border-warning [&:not(.disabled):not(:focus-within)]:hover:border-hover-warning;

  &:not(.disabled):focus-within {
    --bq-ring-color-focus: theme(stroke.warning);

    @apply border-active-warning;
  }
}

/* -------------------------------------------------------------------------- */
/*                              Native HTML Input                             */
/* -------------------------------------------------------------------------- */

.bq-select__control--input {
  @apply flex-auto cursor-inherit select-none appearance-none bg-inherit font-inherit text-[length:inherit] text-inherit;
  @apply m-0 border-none p-0 min-bs-[var(--bq-select--icon-size)] min-is-0 focus:outline-none focus-visible:outline-none;

  box-shadow: none;
  font-weight: inherit;
}

/* -------------------------------------------------------------------------- */
/*                                Clear button                                */
/* -------------------------------------------------------------------------- */

.bq-select__control--clear::part(button) {
  // Since the clear button is inside the input group control,
  // we need to reset the focus ring styles
  --bq-ring-width: initial;
  --bq-ring-offset-width: initial;
  --bq-ring-color-focus: initial;
}

/* -------------------------------------------------------------------------- */
/*                              Prefix and suffix                             */
/* -------------------------------------------------------------------------- */

.bq-select__control--prefix,
.bq-select__control--suffix {
  @apply pointer-events-none flex items-center text-[color:var(--bq-select--text-color)];
}

.bq-select__control--prefix {
  @apply me-[--bq-select--gap];
}

.bq-select__control--suffix {
  @apply ms-[--bq-select--gap] transition-transform duration-300 ease-in-out;
}

.bq-select__tags {
  @apply me-xs2 flex flex-1 gap-xs2;

  bq-tag,
  ::slotted(bq-tag) {
    @apply inline-flex;
  }

  bq-tag::part(text) {
    @apply text-nowrap leading-small;
  }
}

/* -------------------------------------------------------------------------- */
/*                         Slotted and internal icons                         */
/* -------------------------------------------------------------------------- */

bq-icon,
::slotted(bq-icon) {
  --bq-icon--size: var(--bq-select--icon-size) !important;
}
